import { gaEventManager } from './ga-event-manager';
import { gtmEventManager } from './gtm-event-manager';
import type {
  Route,
  UserData,
  ScrollData,
  PortfolioCreationData,
  TransactionCompletedData,
  SearchData,
  MenuClickData,
  BurgerMenuToggleData,
  FeedClickData,
  CreatePostData,
  FollowActiveData,
  FollowInvestorData,
  SendMessageInvestorData,
  ClickAlertData,
  ClickTopPortfoliosData,
  InteractionArticleData,
  PostImpressionData,
  PortfolioInteractionData,
  InteractionEventData,
  OnboardingNextStepData,
  LoginData,
  RegisterData,
} from './gtm-data-types';

// This file is responsible for raising analytics events to analytics providers such as Google Analytics.

// This file should not have any implementation details of the analytics provider, only the event raising logic.

// Different analytics providers may require specific formats of events, so the formatting should be handled in the provider-specific manager objects/modules, rather than here.

/**
 * Guards against exceptions thrown when raising an event.
 * @param func The function to be called
 */
function raiseEventGuard<TArgs extends unknown[], TReturn = void>(
  func: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn | void {
  return (...args: TArgs) => {
    try {
      return func(...args);
    } catch (ex: unknown) {
      console.error('Failed to raise event', ex);
    }
  };
}

/////////////////////////////////////////////////////////////////////
// Google analytics events
///////////////////////////////////////////////////

/**
 * Raises an event to indicate that portfolio creation has started.
 * This should not be confused with opening the portfolio creation modal.
 * This event should be raised when an API call is made to create a portfolio.
 */
function raisePortfolioCreationStartedEvent() {
  gaEventManager.raisePortfolioCreationStartedEvent();
}

/**
 * Raises an event to indicate that a portfolio has been successfully created.
 */
function raisePortfolioCreatedEvent() {
  gaEventManager.raisePortfolioCreatedEvent();
}

/**
 * Raises an event to indicate that portfolio creation has failed.
 * This event should be raised when an API call to create a portfolio fails.
 */
function raisePortfolioCreationFailedEvent() {
  gaEventManager.raisePortfolioCreationFailedEvent();
}

/////////////////////////////////////////////////////////////////////
// GTM events
///////////////////////////////////////////////////

/**
 * Raises a page view event in GTM
 * @param route The current route object
 * @param previousPath The previous path (optional)
 */
function raisePageViewEvent(route: Route, previousPath?: string) {
  gtmEventManager.raisePageViewEvent(route, previousPath);
}

/**
 * Raises a user identify event in GTM
 * @param userData The user data to send with the event
 */
function raiseUserIdentifyEvent(userData: UserData) {
  gtmEventManager.raiseUserIdentifyEvent(userData);
}

/**
 * Raises a scroll event in GTM
 * @param scrollData The scroll tracking data
 */
function raiseScrollEvent(scrollData: ScrollData) {
  gtmEventManager.raiseScrollEvent(scrollData);
}

/**
 * Raises a search event in GTM
 * @param searchData The search data
 */
function raiseSearchEvent(searchData: SearchData) {
  gtmEventManager.raiseSearchEvent(searchData);
}

/**
 * Raises a portfolio creation event in GTM
 * @param portfolioData The portfolio creation data
 */
function raisePortfolioCreationEvent(portfolioData: PortfolioCreationData) {
  gtmEventManager.raisePortfolioCreationEvent(portfolioData);
}

/**
 * Raises a transaction completed event in GTM
 * @param transactionData The transaction data
 */
function raiseTransactionCompletedEvent(
  transactionData: TransactionCompletedData
) {
  gtmEventManager.raiseTransactionCompletedEvent(transactionData);
}

/**
 * Raises an event when the search bar is clicked
 */
function raiseClickSearchBarEvent() {
  gtmEventManager.raiseClickSearchBarEvent();
}

/**
 * Raises an event when a main menu item is clicked
 * @param menuData The menu click data
 */
function raiseClickMenuPrincipalEvent(menuData: MenuClickData) {
  gtmEventManager.raiseClickMenuPrincipalEvent(menuData);
}

/**
 * Raises an event when the burger menu is clicked
 */
function raiseToggleMenuBurgerEvent(burgerMenuData: BurgerMenuToggleData) {
  gtmEventManager.raiseToggleMenuBurgerEvent(burgerMenuData);
}

/**
 * Raises an event when a feed view is clicked
 * @param feedData The feed data including the feed name
 */
function raiseClickViewFeedEvent(feedData: FeedClickData) {
  gtmEventManager.raiseClickViewFeedEvent(feedData);
}

/**
 * Raises an event when a post is created
 * @param postData The post data including name, links and tags
 */
function raiseCreatePostEvent(postData: CreatePostData) {
  gtmEventManager.raiseCreatePostEvent(postData);
}

/**
 * Raises an event when a user follows an active (stock, crypto, etc)
 * @param followData The follow data including active name and category
 */
function raiseFollowActiveEvent(followData: FollowActiveData) {
  gtmEventManager.raiseFollowActiveEvent(followData);
}

/**
 * Raises an event when a user follows an investor
 * @param followData The follow data including investor name and type
 */
function raiseFollowInvestorEvent(followData: FollowInvestorData) {
  gtmEventManager.raiseFollowInvestorEvent(followData);
}

/**
 * Raises an event when a user sends a message to an investor
 * @param messageData The message data including sender and receptor names
 */
function raiseSendMessageInvestorEvent(messageData: SendMessageInvestorData) {
  gtmEventManager.raiseSendMessageInvestorEvent(messageData);
}

/**
 * Raises an event when a user clicks on an alert
 * @param alertData The alert data including active name and category
 */
function raiseClickAlertEvent(alertData: ClickAlertData) {
  gtmEventManager.raiseClickAlertEvent(alertData);
}

/**
 * Raises an event when a user clicks on a top portfolio
 * @param portfolioData The portfolio data including name and position
 */
function raiseClickTopPortfoliosEvent(portfolioData: ClickTopPortfoliosData) {
  gtmEventManager.raiseClickTopPortfoliosEvent(portfolioData);
}

/**
 * Raises an event when a user interacts with an article
 * @param interactionData The interaction data including article details and interaction type
 */
function raiseInteractionArticleEvent(interactionData: InteractionArticleData) {
  gtmEventManager.raiseInteractionArticleEvent(interactionData);
}

/**
 * Raises an event when a post is viewed by a user
 * @param impressionData The impression data including post ID and author
 */
function raisePostImpressionEvent(impressionData: PostImpressionData) {
  gtmEventManager.raisePostImpressionEvent(impressionData);
}

/**
 * Raises an event when a user interacts with a portfolio
 * @param interactionData The interaction data including portfolio details and interaction type
 */
const raisePortfolioInteractionEvent = raiseEventGuard(
  (interactionData: PortfolioInteractionData) => {
    gtmEventManager.raisePortfolioInteractionEvent(interactionData);
  }
);

/**
 * Raises an event when a user creates a comment on an article
 * @param interactionData The interaction data including category, action and post name
 */
function raiseCreateCommentEvent(interactionData: InteractionEventData) {
  gtmEventManager.raiseInteractionEvent(interactionData);
}

/**
 * Raises an event when a user creates a comment on an article
 * @param interactionData The interaction data including category, action and post name
 */
function raiseOnboardingNextStepEvent(
  onboardingNextStepData: OnboardingNextStepData
) {
  gtmEventManager.raiseOnboardingNextStepEvent(onboardingNextStepData);
}

/**
 * Raises an event when a user logs in from the authProvider
 * @param loginData The interaction data including email and username
 */
function raiseLoginEvent(loginData: LoginData) {
  gtmEventManager.raiseLoginEvent(loginData);
}

/**
 * Raises an event when a user registers from the authProvider
 * @param registerData The interaction data including email and username
 */
function raiseRegisterEvent(registerData: RegisterData) {
  gtmEventManager.raiseRegisterEvent(registerData);
}

/**
 * The event manager object is responsible for raising events to indicate that specific application behaviors have occurred.
 * This object should be used to raise analytics events when certain application behaviors occur.
 */
export const eventManager = {
  raisePortfolioCreationStartedEvent: raiseEventGuard(
    raisePortfolioCreationStartedEvent
  ),
  raisePortfolioCreatedEvent: raiseEventGuard(raisePortfolioCreatedEvent),
  raisePortfolioCreationFailedEvent: raiseEventGuard(
    raisePortfolioCreationFailedEvent
  ),
  // GTM events
  raisePageViewEvent: raiseEventGuard(raisePageViewEvent),
  raiseUserIdentifyEvent: raiseEventGuard(raiseUserIdentifyEvent),
  raiseScrollEvent: raiseEventGuard(raiseScrollEvent),
  raiseSearchEvent: raiseEventGuard(raiseSearchEvent),
  raisePortfolioCreationEvent: raiseEventGuard(raisePortfolioCreationEvent),
  raiseTransactionCompletedEvent: raiseEventGuard(
    raiseTransactionCompletedEvent
  ),
  raiseClickSearchBarEvent: raiseEventGuard(raiseClickSearchBarEvent),
  raiseClickMenuPrincipalEvent: raiseEventGuard(raiseClickMenuPrincipalEvent),
  raiseToggleMenuBurgerEvent: raiseEventGuard(raiseToggleMenuBurgerEvent),
  raiseClickViewFeedEvent: raiseEventGuard(raiseClickViewFeedEvent),
  raiseCreatePostEvent: raiseEventGuard(raiseCreatePostEvent),
  raiseFollowActiveEvent: raiseEventGuard(raiseFollowActiveEvent),
  raiseFollowInvestorEvent: raiseEventGuard(raiseFollowInvestorEvent),
  raiseSendMessageInvestorEvent: raiseEventGuard(raiseSendMessageInvestorEvent),
  raiseClickAlertEvent: raiseEventGuard(raiseClickAlertEvent),
  raiseClickTopPortfoliosEvent: raiseEventGuard(raiseClickTopPortfoliosEvent),
  raiseInteractionArticleEvent: raiseEventGuard(raiseInteractionArticleEvent),
  raisePostImpressionEvent: raiseEventGuard(raisePostImpressionEvent),
  raisePortfolioInteractionEvent,
  raiseCreateCommentEvent: raiseEventGuard(raiseCreateCommentEvent),
  raiseOnboardingNextStepEvent: raiseEventGuard(raiseOnboardingNextStepEvent),
  raiseLoginEvent: raiseEventGuard(raiseLoginEvent),
  raiseRegisterEvent: raiseEventGuard(raiseRegisterEvent),
};
